/**
 * login-id/index.tsx  — Auth0 ACUL screen: "login-id"
 *
 * WHAT THIS SCREEN DOES
 * ---------------------
 * Collects the user's email address OR phone number as the first step in
 * the Identifier First authentication flow.  On submit it calls
 * `loginId.login({ username })`, after which Auth0 decides the next screen:
 *
 *   email   → Auth0 routes to `login-password`  (password-first)
 *           → or `login-passwordless-email-code` (if email passwordless is
 *             the only enabled connection, or the user picks OTP)
 *   phone   → Auth0 routes to `login-passwordless-sms-otp`  (OTP-first)
 *           → or `login-password` (if phone-as-username DB connection enabled)
 *
 * AUTH0 ROUTING NOTE
 * ------------------
 * Auth0 Universal Login determines the next screen from the tenant's
 * connection configuration — this screen cannot force a specific next step.
 * "Use a code / use a password" secondary buttons here are UX hints only.
 * For passwordless routing, see src/utils/navigation.ts.
 *
 * HOW TO EXTEND
 * -------------
 * - To add social login buttons, re-enable <AlternativeLogins /> below.
 * - To add passkey autofill, see the original LoginIdForm.tsx in ./components/.
 */

import { AuthLayout } from "@/components/auth/AuthLayout";
import { IdentifierForm } from "@/components/auth/IdentifierForm";
import { loadIdentifier } from "@/utils/navigation";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";
import { useErrors } from "@auth0/auth0-acul-react/login-id";
import { useLoginIdManager } from "./hooks/useLoginIdManager";

function LoginIdScreen() {
  const {
    loginId,
    texts,
    locales,
    signupLink,
    handleLoginId,
    detectIdentifierType,
  } = useLoginIdManager();

  document.title = texts?.pageTitle || locales?.page?.title || "Log in";
  applyAuth0Theme(loginId);

  // Pull Auth0 errors to show in the identifier form
  const { errors } = useErrors();
  const firstError = errors.byType("auth0")[0]?.message ?? undefined;

  const handleSubmit = (identifier: string) => {
    const identifierType = detectIdentifierType(identifier);
    let tempUserName = identifier;
    if (identifierType === "sms") {
      tempUserName = "+1" + tempUserName;
    }
    handleLoginId({ username: tempUserName });
  };

  return (
    <AuthLayout title="Log in">
      <IdentifierForm
        defaultValue={loadIdentifier()}
        onSubmit={handleSubmit}
        errorMessage={firstError}
        signupHref={signupLink ?? undefined}
      />
    </AuthLayout>
  );
}

export default LoginIdScreen;
